import React, { useState, useEffect } from 'react';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import Checkbox from 'b2b-ui-components-lib/lib/components/checkbox';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Datepicker from 'b2b-ui-components-lib/lib/components/datePicker';
import { isFormValid, numberValidator } from '../../../../common/utils/validator';
import FileUpload from '../fileUpload/fileUpload';
import { DOCUMENT_TYPE_ENUM } from 'home/view/manage-feasibility/constants';
import { formSchema, getDefaultValue, getDefaultCheckBoxValue, ignoreKeys, purchaseOrderExpiryDateKey } from './formControl';
import { getPreviousYearDate, getRange } from 'common/utils/app.utils';
import { FILE_MAX_SIZE, FILE_MAX_SIZE_MSG, PO_MAX_YEAR_BACK } from 'common/constants/app.constants';

const PurchaseDetails = ({ updateParentForm, setFormStatus, order, isOrderConfirmation = false }) => {
  const fileSchema = {
    supportedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    maxFileLimit: FILE_MAX_SIZE,
    documentNames: ['PO_DOCUMENT', 'PO_DOCUMENT_2', 'PO_DOCUMENT_3'],
    documentType: DOCUMENT_TYPE_ENUM.PURCHASE_ORDER,
    allowMultiplePDFs: true
  };

  const [form, setForm] = useState(getDefaultValue(order));
  const [checked, setChecked] = useState(true);
  const [accordionMetadata, setAccordionMetadata] = useState({ isValidated: false });
  const onCheckBoxChange = (value) => {
    setChecked(value);
    if (!value) {
      setForm({
        ...form,
        purchaseOrderNo: { value: '', touched: false, errors: [] },
        purchaseOrderDate: { value: '', touched: false, errors: [] },
        purchaseOrderTotalCharges: { value: '', touched: false, errors: [] }
      });
    } else {
      setForm(formSchema);
    }
  };

  const onPurchaseOrderNo = (value) => {
    setForm({
      ...form,
      purchaseOrderNo: {
        ...form.purchaseOrderNo,
        value,
        touched: true,
        errors: form.purchaseOrderNo.validator(value)
      }
    });
  };
  const onPurchaseOrderDateChange = (value, keyName) => {
    if (value) {
      setForm({
        ...form,
        [keyName]: { ...form[keyName], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [keyName]: {
        ...form[keyName],
        value,
        touched: true,
        errors: keyName === purchaseOrderExpiryDateKey ? [] : ['This field is mandatory']
      }
    });
  };
  const onPurchaseOrderTotalCharges = (value) =>
    setForm({
      ...form,
      purchaseOrderTotalCharges: {
        ...form.purchaseOrderTotalCharges,
        value,
        touched: true,
        errors: form.purchaseOrderTotalCharges.validator(value)
      }
    });

  const onFileUpload = (files) => {
    setForm({ ...form, files });
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, validator, errors: existingError } = formElement;
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({
      ...form,
      [keyName]: { ...form[keyName], value, errors: errors.concat(existingError), touched: true }
    });
  };

  useEffect(() => {
    if (isFormValid(form, ignoreKeys)) {
      setAccordionMetadata({ isValidated: true });
      updateParentForm({ purchaseDetail: form });
      setFormStatus({ purchaseDetail: true });
    } else {
      setAccordionMetadata({ isValidated: false });
      updateParentForm({ purchaseDetail: null });
      setChecked(checked);
      setFormStatus({ purchaseDetail: !checked ? true : false });
    }
  }, [form]);

  return (
    <React.Fragment>
      <Accordion
        title="Purchase Order Details"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() =>
          setAccordionMetadata(({ isEditable }) => ({
            ...accordionMetadata,
            isEditable: !isEditable
          }))
        }
      >
        <div className="accordion_body">
          <Checkbox disabled={true} checked={checked} onChange={onCheckBoxChange}>
            <span className="check_text">Add PO details in my bill</span>
          </Checkbox>

          {checked && (
            <div className="order_details_form">
              <div className="row field_row">
                <div className="flg-lg-12 flg-md-12">
                  <div className="form_area">
                    <div className="row">
                      <div className="flg-lg-6 flg-md-6">
                        <Input
                          label="Purchase Order Number"
                          disabled={isOrderConfirmation}
                          required
                          onBlur={() => onFieldBlur(form.purchaseOrderNo, 'purchaseOrderNo')}
                          errors={form.purchaseOrderNo.errors}
                          touched={form.purchaseOrderNo.touched}
                          value={form.purchaseOrderNo.value}
                          onChange={onPurchaseOrderNo}
                        />
                      </div>

                      <div className={`flg-lg-6 flg-md-6 ${isOrderConfirmation ? 'disabled' : ''}`}>
                        <Datepicker
                          name="datepicker"
                          label="Purchase Order Date"
                          disabled={isOrderConfirmation}
                          required={true}
                          errors={form.purchaseOrderDate.errors}
                          touched={form.purchaseOrderDate.touched}
                          value={form.purchaseOrderDate.value}
                          onChange={(value) => onPurchaseOrderDateChange(value, 'purchaseOrderDate')}
                          maxDate={new Date()}
                          minDate={getPreviousYearDate(PO_MAX_YEAR_BACK)}
                          years={getRange(getPreviousYearDate(PO_MAX_YEAR_BACK).getFullYear(), new Date().getFullYear())}
                        />
                      </div>

                      <div className="flg-lg-6 flg-md-6">
                        <Input
                          label="Purchase Order Total Charges"
                          disabled={isOrderConfirmation}
                          required
                          onBlur={() => onFieldBlur(form.purchaseOrderTotalCharges, 'purchaseOrderTotalCharges')}
                          errors={form.purchaseOrderTotalCharges.errors}
                          touched={form.purchaseOrderTotalCharges.touched}
                          value={form.purchaseOrderTotalCharges.value}
                          onChange={onPurchaseOrderTotalCharges}
                        />
                      </div>

                      <div className={`flg-lg-6 flg-md-6 ${isOrderConfirmation ? 'disabled' : ''}`}>
                        <Datepicker
                          name="datepicker"
                          label="Purchase Order Expiry Date (Optional)"
                          disabled={isOrderConfirmation}
                          required={true}
                          errors={form.purchaseOrderExpiryDate.errors}
                          touched={form.purchaseOrderExpiryDate.touched}
                          value={form.purchaseOrderExpiryDate.value}
                          onChange={(value) => onPurchaseOrderDateChange(value, 'purchaseOrderExpiryDate')}
                          maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 5))}
                          minDate={new Date()}
                          years={getRange(new Date().getFullYear(), new Date().getFullYear() + 5)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flg-lg-6 flg-md-6">
                  <div className="form_area">
                    <div className="row"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {checked && (
          <div className={isOrderConfirmation ? 'accordion_footer disabled' : 'accordion_footer'}>
            <FileUpload
              onFileUploadSuccess={onFileUpload}
              label="Upload Purchase Order Document"
              supportedLabel="Supported file format is .PDF, .PNG, .JPG"
              limitLabel={FILE_MAX_SIZE_MSG}
              fileSchema={fileSchema}
              isFileHaveValue={form && form.files && form.files.length > 0 ? true : false}
              fileArray={form && form.files}
              isOrderConfirmation={isOrderConfirmation}
              multiple={true}
            ></FileUpload>
          </div>
        )}
      </Accordion>
    </React.Fragment>
  );
};

export default PurchaseDetails;
